import { useQuery, useMutation } from "@tanstack/react-query"

import type { QueryFunctionContext, UseQueryResult } from "@tanstack/react-query"

import { STANCE_FILTER_KEY, PRIORITY_FILTER_KEY } from "shared/imports/sharedConstants"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

import {
    TEAM_PERMISSION_EVERYONE,
    TEAM_PERMISSION_LIMITED_JUST_ME,
    TEAM_PERMISSION_LIMITED_TEAMS,
} from "@/components/TeamPermissioningFormSection/TeamPermissioningFormSection"
import { formatTagDictMutation } from "@/pages/helpers"
import { updateAssignments } from "@/services/assignments"

import type { FormValues } from "@/pages/issue-management/new/types"
import type { Issue, CustomField } from "@/types/djangio"

type Priority = { id: number; priority: string }
type Stance = { id: number; stance: string }

const UserActionType = DjangIO.app.useractions.types.UserActionType
const IssueManagement = DjangIO.app.userdata.models.IssueManagement
const ActionCenter = DjangIO.app.grassroots.models.GrassrootsActionCenterSettings

const issuesKeys = {
    all: [{ _model: "issue_management" }] as const,
    detail: ({ projectId }: { projectId: string }) => [{ ...issuesKeys.all[0], projectId }] as const,
}

const fetchIssue = async ({
    queryKey,
}: QueryFunctionContext<ReturnType<(typeof issuesKeys)["detail"]>>): Promise<Issue> => {
    const [{ projectId }] = queryKey

    return IssueManagement.objects
        .filter({
            dehydrate_extra: ["parent_name", "has_children"],
        })
        .get(projectId)
        .GET()
}

export const useIssue = (projectId: string) =>
    useQuery({
        queryKey: issuesKeys.detail({ projectId }),
        queryFn: fetchIssue,
        enabled: Boolean(projectId),
        select: (data) => {
            const parent = data.parent ? IssueManagement.idFromResourceUri(data.parent) : ""
            const limitedToTeams: number[] = data.limited_to_teams.map((teamUri: string) =>
                DjangIO.app.userdata.models.Team.idFromResourceUri(teamUri),
            )
            const getTeamPermissioning = () => {
                if (data.is_limited_to_creator) {
                    return TEAM_PERMISSION_LIMITED_JUST_ME
                }

                return data.limited_to_teams.length ? TEAM_PERMISSION_LIMITED_TEAMS : TEAM_PERMISSION_EVERYONE
            }
            const actionCenters = data.action_centers?.map((uri) => String(ActionCenter.idFromResourceUri(uri)))

            return {
                name: data.name,
                parent,
                avatarType: data.image_url ? ("image" as const) : ("icon" as const),
                imageUrl: data.image_url,
                icon: data.icon,
                canBeTaggedToInteractions: data.can_be_tagged_to_interactions,
                canBeTaggedToEvents: data.can_be_tagged_to_events,
                isGrassrootsIssue: Boolean(data.action_centers && data.action_centers.length),
                actionCenters,
                grassrootsPageLayout: String(data.grassroots_page_layout_type),
                shortDescription: data.short_description,
                longDescription: data.long_description,
                teamPermissioning: getTeamPermissioning(),
                limitedToTeams,
                creator: data.creator,
                isVisibleToPartners: data.is_visible_to_partners,
            }
        },
    })

const saveIssue = (values: FormValues, projectId?: string) => {
    const getActionCenters = () => {
        if (!values.isGrassrootsIssue) {
            return []
        }

        if (values.isGrassrootsIssue && Userdata.has_singular_action_center && !values.actionCenters.length) {
            return [ActionCenter.resourceUriFromId(Userdata.action_center_settings_id)]
        }

        return values.actionCenters.map((id) => ActionCenter.resourceUriFromId(id))
    }

    const getLimitedToTeams = () => {
        if (values.teamPermissioning === "everyone") {
            return []
        }
        return values.limitedToTeams?.map((team: number | { resource_uri: string }) =>
            typeof team === "number" ? DjangIO.app.userdata.models.Team.resourceUriFromId(team) : team.resource_uri,
        )
    }

    const data: Partial<Issue> = {
        action_centers: getActionCenters(),
        parent: values.parent ? DjangIO.app.userdata.models.Project.resourceUriFromId(values.parent) : null,
        image_url: values.avatarType === "image" ? values.imageUrl : null,
        icon: values.icon,
        name: values.name,
        // If the User has default Teams, but we then set it visible to "Everyone", then limitedToTeams is undefined.
        // In that case, we actually need to send an empty array to the backend so that any existing value gets cleared.
        limited_to_teams: getLimitedToTeams(),
        long_description: values.longDescription,
        short_description: values.shortDescription,
        grassroots_page_layout_type: values.grassrootsPageLayout && Number(values.grassrootsPageLayout),
        priority: values.priority && Number(values.priority),
        can_be_tagged_to_interactions: values.canBeTaggedToInteractions,
        can_be_tagged_to_events: values.canBeTaggedToEvents,
        is_limited_to_creator:
            Userdata.can_only_create_single_user_issues || values.teamPermissioning === TEAM_PERMISSION_LIMITED_JUST_ME,
        is_visible_to_partners: values.isVisibleToPartners,

        // default
        ...(!projectId && {
            boards: [],
            categories: [],
            creator_edit_only: false,
            creator: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            is_carryover: true,
            management_statistics: {},
            organizations: [DjangIO.app.userdata.models.Organization.resourceUriFromId(Userdata.organization_id)],
            participants: [
                {
                    text: "You",
                    id: Userdata.id,
                    resource_uri: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                    default: true,
                },
            ],
            is_issue: true,
            remove_with_stance: true,
            search_terms: [],
            teams: [],
        }),
    }

    // update existing Issue
    if (projectId) {
        return IssueManagement.objects.get(projectId).update(data).PATCH()
    }

    // create new Issue
    return IssueManagement.objects.create(data).POST()
}

type RelatedData = {
    project: string
    organization: string
    data_type: number
    last_user: string
}

const DataDescription = DjangIO.app.userdata.models.DataDescription

const saveDataDescription = (
    values: FormValues,
    relatedData: RelatedData,
    customFields: CustomField[],
    id?: number,
) => {
    const tagDict = formatTagDictMutation(values.tagDict, customFields)

    const data = {
        tag_dict: tagDict,
        text: values.description,
        ...(id ? { last_user: relatedData.last_user } : relatedData),
    }

    const cacheInvalidationParams = {
        endpoints: [DjangIO.newResourceFromDataType(relatedData.data_type).endpoint],
        shouldClearFn: (url) => url.includes("tag_dict"),
    }

    if (id) {
        return DataDescription.objects
            .filter({ full_dehydrate_last_text_updated_user: true })
            .get(id)
            .update(data)
            .PATCH({ cacheInvalidationParams })
    }

    return DataDescription.objects
        .filter({ full_dehydrate_last_text_updated_user: true })
        .create(data)
        .POST({ cacheInvalidationParams })
}

const LegislativePriority = DjangIO.app.userdata.models.LegislativePriority

const savePriority = ({
    issueId,
    values,
    priority,
    relatedData,
}: {
    issueId: number
    values: FormValues
    priority?: Priority
    relatedData: RelatedData
}) => {
    const priorityChanged = values.priority !== priority?.priority

    if (priorityChanged) {
        const newPriority = values.priority || 0

        const cacheInvalidationParams = {
            endpoints: [DjangIO.newResourceFromDataType(relatedData.data_type).endpoint],
            shouldClearFn: (url: string) => url.includes(PRIORITY_FILTER_KEY),
        }

        sendUserActionPost({
            userActionType: priority?.id ? UserActionType.priority_updated : UserActionType.priority_taken,
            quorumDataType: relatedData.data_type,
            regions: [undefined],
        })

        if (priority?.id) {
            // update (PATCH) it
            const data = {
                priority: newPriority,
                archived: false,
                last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            }

            return LegislativePriority.objects.get(priority.id).update(data).PATCH({ cacheInvalidationParams })
        }

        // create (POST) it
        const data = {
            priority: newPriority,
            data_type: relatedData.data_type,
            [(DjangIO.app.models.QuorumDataType.by_value(relatedData.data_type) as any).related_field]:
                DjangIO.newResourceFromDataType(relatedData.data_type).foreignKey(issueId),
            last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id),
        }

        return LegislativePriority.objects.create(data).POST({ cacheInvalidationParams })
    }

    return Promise.resolve(null)
}

const LegislativeStance = DjangIO.app.userdata.models.LegislativeStance

const saveStance = ({
    issueId,
    values,
    stance,
    relatedData,
}: {
    issueId: number
    values: FormValues
    stance?: Stance
    relatedData: RelatedData
}) => {
    const stanceChanged = values.stance !== stance?.stance

    if (stanceChanged) {
        const newStance = values.stance || 0

        const cacheInvalidationParams = {
            endpoints: [DjangIO.newResourceFromDataType(relatedData.data_type).endpoint],
            // In the case the URL constants a stance filter, clear the cache.
            // In the case the URL contains a list filter, also clear the cache.
            // This is so search terms in bill tracking projects on the mobile app
            // can update.
            shouldClearFn: (url: string) => url.includes(STANCE_FILTER_KEY) || url.includes("in_list"),
        }

        sendUserActionPost({
            userActionType: stance?.id ? UserActionType.stance_updated : UserActionType.stance_taken,
            quorumDataType: relatedData.data_type,
            regions: [undefined],
        })

        if (stance?.id) {
            // update (PATCH) it
            const data = {
                stance: newStance,
                archived: false,
                last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            }

            return LegislativeStance.objects.get(stance.id).update(data).PATCH({ cacheInvalidationParams })
        }

        // create (POST) it
        const data = {
            stance: newStance,
            data_type: relatedData.data_type,
            [(DjangIO.app.models.QuorumDataType.by_value(relatedData.data_type) as any).related_field]:
                DjangIO.resourceFromDataType(relatedData.data_type).foreignKey(issueId),
            last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id),
        }

        return LegislativeStance.objects.create(data).POST({ cacheInvalidationParams })
    }

    return Promise.resolve(null)
}

type MutateFnArgs = {
    values: FormValues
    dataDescriptionId?: number
    priority?: Priority
    stance?: Stance
    assignments: UseQueryResult
}

export const useMutateIssue = (customFields: CustomField[], projectId?: string) =>
    useMutation({
        mutationFn: async ({ values, dataDescriptionId, priority, stance, assignments }: MutateFnArgs) => {
            const issue = await saveIssue(values, projectId)

            sendUserActionPost({
                userActionType: projectId ? UserActionType.issue_edited : UserActionType.issue_created,
                description: issue.name,
            })

            const relatedData = {
                project: DjangIO.app.userdata.models.Project.resourceUriFromId(issue.id),
                organization: DjangIO.app.userdata.models.Organization.resourceUriFromId(Userdata.organization_id),
                data_type: DjangIO.app.models.QuorumDataType.project.value,
                last_user: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            }

            const dataDescriptionRequest = saveDataDescription(values, relatedData, customFields, dataDescriptionId)
            const priorityRequest = savePriority({ issueId: issue.id, values, priority, relatedData })
            const stanceRequest = saveStance({ issueId: issue.id, values, stance, relatedData })
            const assignmentsRequest = updateAssignments({
                values,
                assignments,
                entityId: issue.id,
                assignmentType: DjangIO.app.userdata.types.AssignmentType.project,
                quorumDataTypeEnum: DjangIO.app.models.QuorumDataType.project,
            })

            await Promise.allSettled([dataDescriptionRequest, priorityRequest, stanceRequest, assignmentsRequest])

            return issue
        },
    })
